/* eslint-disable max-len */
import { Injectable } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { BehaviorSubject, EMPTY, Observable, asyncScheduler, combineLatest, merge, of } from 'rxjs';
import {
  auditTime,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ComponentEffects, logCatchError } from '@mhe/reader/common';
import {
  Bonsai,
  BulkTopicsResponseRow,
  SpineItem,
  Tag,
  TagLocation,
  TagNode,
  TagType,
  TocItem,
} from '@mhe/reader/models';
import { FeatureTogglesService } from '@mhe/reader/features';
import { ReadiatorBridgeService } from '@mhe/reader/features/readiator-bridge';

import { MediatorUtils } from './mediator-utils';
import { DFAService } from '@mhe/reader/core/dfa-api/dfa.service';
import {
  TopicsStore,
  buildTopicTocReverseMap,
  summarizeTopicTocReverseMap,
  TopicTocReverseMapEntry,
} from '@mhe/reader/components/topics/state';
import * as topicsActions from '@mhe/reader/components/topics/state/topics.actions';
import { EpubLibCFIService } from '@mhe/reader/features/annotation';
import { NavigationStore } from '@mhe/reader/components/navigation';
import * as navigationActions from '@mhe/reader/components/navigation/state/navigation.actions';
import { TocStore } from '@mhe/reader/components/toc';
import * as tocActions from '@mhe/reader/components/toc';
import {
  ReaderConfigStore,
  ReaderStore,
} from '@mhe/reader/components/reader/state';
import { ElasticSearchService } from './elastic-search.service';
import { DfaSearchNode } from '../components/elastic-search/elastic-search.model';
import { TopicMetadataStore, TopicResolutionHints } from '../components/topics/state/topic-metadata.store';
import * as readerActions from '@mhe/reader/components/reader/state/reader.actions';

function isDebugMode(): boolean {
  return typeof window !== 'undefined' && localStorage.getItem('readerx_debug') === 'true';
}

function emitDiagnostics(label: string, data: any): void {
  if (isDebugMode()) {
    // console.log(`[TopicsMediator] ${label}`, data);
  }
}

function calculateAverageCandidateScore(entries: TopicTocReverseMapEntry[]): number {
  if (!entries.length) return 0;
  const totalScore = entries.reduce((sum, entry) => sum + (entry.bestCandidate?.score || 0), 0);
  return Math.round((totalScore / entries.length) * 100) / 100;
}

@Injectable()
export class TopicsMediator extends ComponentEffects {
  private readonly topicsActions$ = this.topicsStore.actions$;
  private readonly navigationActions$ = this.navigationStore.actions$;
  private readonly readerActions$ = this.readerStore.actions$;
  private readonly hydratedSubjectIds = new Set<string>();

  /** Carries the latest navigation context hints so syncTopicMetadata$ can pass them to the resolver. */
  private readonly currentNavigationHints$ = new BehaviorSubject<TopicResolutionHints>({});

  constructor(
    private readonly readerConfigStore: ReaderConfigStore,
    private readonly dfaService: DFAService,
    private readonly topicsStore: TopicsStore,
    private readonly readerStore: ReaderStore,
    private readonly navigationStore: NavigationStore,
    private readonly mediatorUtils: MediatorUtils,
    private readonly epubLibCfiService: EpubLibCFIService,
    private readonly tocStore: TocStore,
    private readonly elasticSearchService: ElasticSearchService,
    private readonly topicMetadataStore: TopicMetadataStore,
    private readonly featureTogglesService: FeatureTogglesService,
    private readonly readiatorBridge: ReadiatorBridgeService,
  ) {
    super();
  }

  readonly setSubjects = combineLatest([
    this.readerConfigStore.topicsEndpoint$,
    this.readerConfigStore.idmToken$,
  ]).pipe(
    filter(([_, token]) => Boolean(token)),
    switchMap(([topicsEndpoint]) => {
      return this.dfaService.requestSubjectsOrTags(topicsEndpoint).pipe(
        take(1),
        tap((tags) => {
          this.topicsStore.setLoading(false);
          this.topicsStore.setSubjects(tags);
        }),
      );
    }),
    withLatestFrom(
      this.readerConfigStore.dfaCategoryUUID$,
      this.readerConfigStore.dfaTopicUUID$,
      this.readerConfigStore.subjectTopicsEndpoint$,
      this.readerConfigStore.bulkTopicsEndpoint$,
      this.featureTogglesService.bulkHytHydration$,
    ),
    switchMap(([
      _,
      dfaCategoryUUID,
      dfaTopicUUID,
      subjectTopicsEndpoint,
      bulkTopicsEndpoint,
      bulkHydrationEnabled,
    ]) => {
      const shouldUseBulkHydration =
        bulkHydrationEnabled === true &&
        Boolean(bulkTopicsEndpoint);

      const hydration$ = shouldUseBulkHydration
        ? this.hydrateFromBulkEndpoint(bulkTopicsEndpoint)
        : of(null);

      return hydration$.pipe(
        switchMap(() => {
          const shouldRedirect =
            dfaCategoryUUID !== undefined && dfaTopicUUID !== undefined;

          if (shouldRedirect && shouldUseBulkHydration) {
            this.topicsStore.dispatch(topicsActions.navigateToTopic());
            return EMPTY;
          }

          if (shouldRedirect) {
            return this.getDfaLocations(
              subjectTopicsEndpoint,
              dfaCategoryUUID,
              shouldRedirect,
            ).pipe(take(1));
          }
          return EMPTY;
        }),
      );
    }),
    logCatchError('setSubjects', false, this.topicsStore.setErrorLoadingSubjects),
  );

  private hydrateFromBulkEndpoint(
    bulkTopicsEndpoint: string,
  ): Observable<unknown> {
    this.topicsStore.setLoading(true);

    return this.dfaService.requestBulkTopics(bulkTopicsEndpoint).pipe(
      take(1),
      withLatestFrom(this.topicsStore.topicsTree$),
      tap(([bulkRows, topicsTree]) => {
        const subjectIds = this.getUnhydratedSubjectIds(topicsTree);
        const rowsByParentUuid = this.createBulkRowsMap(bulkRows);
        const missingSubjectIds = subjectIds.filter(
          (subjectId) => !rowsByParentUuid.has(subjectId),
        );

        if (missingSubjectIds.length > 0) {
          throw new Error(
            `Bulk HYT endpoint returned incomplete payload. Missing subjects: ${missingSubjectIds.join(',')}`,
          );
        }

        const subjectTagsResponses: Array<{ subjectId: string, tags: Tag[] }> = [];

        subjectIds.forEach((subjectId) => {
          const tags = rowsByParentUuid.get(subjectId) || [];
          this.sortDfaTags(tags);
          this.hydratedSubjectIds.add(subjectId);
          subjectTagsResponses.push({ subjectId, tags });
        });

        this.topicsStore.setBulkTopics(subjectTagsResponses);
      }),
      catchError((error) => {
        console.error('Bulk HYT hydration failed. Falling back to lazy hydration.', error);
        this.topicsStore.setLoading(false);

        return of(null);
      }),
    );
  }

  private createBulkRowsMap(
    bulkRows: BulkTopicsResponseRow[],
  ): Map<string, Tag[]> {
    return bulkRows.reduce((mapByParentUuid, row) => {
      mapByParentUuid.set(row.parentUUID, row.tags || []);
      return mapByParentUuid;
    }, new Map<string, Tag[]>());
  }

  readonly init$ = this.effect(() => {
    return merge(this.topicsActions$).pipe(
      ofType(topicsActions.init),
      withLatestFrom(
        this.readerConfigStore.dfaCategoryUUID$,
        this.readerConfigStore.dfaTopicUUID$,
        this.topicsStore.initialSubjectId$,
      ),
      filter(
        ([, categoryId, subjectId, initialSubjectId]) =>
          !!categoryId && !!subjectId && initialSubjectId === undefined,
      ),
      take(1),
      tap(([, categoryId, subjectId]) => {
        this.topicsStore.updateExpandedSubjects({ id: categoryId as string });
        this.topicsStore.setInitialSubjectId(subjectId);
      }),
      logCatchError('init$'),
    );
  });

  readonly navigateToTopic$ = this.effect(() => {
    return merge(this.topicsActions$).pipe(
      ofType(topicsActions.navigateToTopic),
      withLatestFrom(
        this.topicsStore.topicsTree$,
        this.readerConfigStore.dfaTopicUUID$,
        this.readerConfigStore.cfi$,
      ),
      filter(([, topicsTree]) => Object.values(topicsTree).length > 0),
      tap(([, topicsTree, topicId, cfi]) => {
        const tagNode = topicsTree[topicId as string] as TagNode;
        if (tagNode) {
          this.topicsStore.setCurrentTopicNode({
            node: tagNode,
          });
          const location = tagNode.locations?.find(
            (location) => location.cfi === cfi,
          ) as TagLocation;
          this.topicsStore.dispatch(topicsActions.setTopicInToc({ location }));
        } else {
          console.warn(
            ` - unable to navigate user since there's no tag featuring topicId such as '${topicId}' into topics tree`,
          );
        }
      }),
      logCatchError('navigateToTopic$'),
    );
  });

  getDfaLocations = (
    subjectTopicsEndpoint,
    subjectId,
    shouldRedirect?,
  ): Observable<any> => {
    this.hydratedSubjectIds.add(subjectId);

    return this.dfaService.requestSubjectsOrTags(subjectTopicsEndpoint, subjectId).pipe(
      take(1),
      tap((tags) => {
        this.sortDfaTags(tags);
        this.topicsStore.setTopics({ subjectId, tags });
        if (shouldRedirect) { this.topicsStore.dispatch(topicsActions.navigateToTopic()); }
      }),
      catchError(() => {
        this.topicsStore.setSubjectLoadingState({ subjectId, loading: false });
        return EMPTY;
      }),
    );
  };

  /**
   * Captures the navigation hint (anchor UUID + spine index) from every spine change
   * so that subsequent metadata resolution can use them.
   */
  readonly captureNavigationHints$ = this.effect(() =>
    this.readerActions$.pipe(
      ofType(readerActions.setSpineItem),
      tap(({ spineItem, hash }) => {
        const anchorUuid = hash ? hash.replace(/^#/, '') : undefined;
        this.currentNavigationHints$.next({
          targetAnchorUuid: anchorUuid,
          currentSpineIndex: (spineItem).index,
        });
      }),
      logCatchError('captureNavigationHints$'),
    ),
  );

  readonly rebuildTopicTocReverseMap$ = this.effect(() =>
    combineLatest([
      this.topicsStore.topicsTree$,
      this.readerStore.toc$,
    ]).pipe(
      filter(([topicsTree, toc]) => Object.keys(topicsTree || {}).length > 0 && Boolean(toc?.length)),
      map(([topicsTree, toc]) => {
        const startTime = performance.now();
        return { reverseMap: buildTopicTocReverseMap(topicsTree as Record<string, TagNode>, toc), startTime };
      }),
      tap(({ reverseMap, startTime }) => {
        const rebuildTime = Math.round((performance.now() - startTime) * 100) / 100;
        this.topicsStore.setTopicTocReverseMap(reverseMap);
        emitDiagnostics('topicTocReverseMap rebuilt', {
          entries: reverseMap.entries.length,
          rebuildTimeMs: rebuildTime,
          summary: summarizeTopicTocReverseMap(reverseMap),
          avgScore: calculateAverageCandidateScore(reverseMap.entries),
        });
      }),
      logCatchError('rebuildTopicTocReverseMap$'),
    ),
  );

  readonly syncTopicMetadata$ = this.effect(() => {
    return combineLatest([
      this.topicsStore.topicsTree$,
      this.topicsStore.topicTocReverseMap$,
      this.topicsStore.selectedLocation$,
      this.topicsStore.currentSpineId$,
      this.readerConfigStore.subjectTopicsEndpoint$,
      this.currentNavigationHints$,
      this.tocStore.activeTocItem$.pipe(startWith(undefined)),
    ]).pipe(
      filter(([, , , currentSpineId]) => Boolean(currentSpineId)),
      map(([
        topicsTree,
        topicTocReverseMap,
        selectedLocation,
        currentSpineId,
        subjectTopicsEndpoint,
        hints,
        activeTocItem,
      ]) => {
        const tocAnchorUuid =
          activeTocItem?.id && activeTocItem.id !== currentSpineId
            ? activeTocItem.id
            : undefined;

        const mergedHints: TopicResolutionHints = {
          ...hints,
          targetAnchorUuid: hints.targetAnchorUuid || tocAnchorUuid,
          targetTitle: hints.targetTitle || activeTocItem?.label,
          targetSpineId: hints.targetSpineId || activeTocItem?.spineItem?.id,
        };

        const resolutionKey = [
          currentSpineId || '',
          selectedLocation?.cfi || '',
          selectedLocation?.anchorUuid || '',
          mergedHints.targetCfi || '',
          mergedHints.targetAnchorUuid || '',
          mergedHints.targetSpineId || '',
          topicTocReverseMap?.entries?.length || 0,
        ].join('|');

        return {
          topicsTree,
          topicTocReverseMap,
          selectedLocation,
          currentSpineId,
          subjectTopicsEndpoint,
          hints: mergedHints,
          resolutionKey,
        };
      }),
      filter(({ selectedLocation, hints, topicTocReverseMap }) => {
        const hasSelectedLocation = Boolean(selectedLocation?.cfi || selectedLocation?.anchorUuid);
        const hasHintedLocation = Boolean(
          hints.targetCfi ||
          hints.targetAnchorUuid ||
          hints.targetSpineId,
        );
        const hasReverseMap = Boolean(topicTocReverseMap?.entries?.length);

        return hasSelectedLocation || (hasHintedLocation && hasReverseMap);
      }),
      auditTime(0, asyncScheduler),
      distinctUntilChanged((previous, current) => previous.resolutionKey === current.resolutionKey),
      // Metadata/reverse-map is built from hydrated HYT subjects. In lazy mode
      // subjects are hydrated on direct HYT UI interaction (expand/select). When
      // the `bulk_hyt_hydration` toggle is on, all subjects are hydrated eagerly
      // via the bulk endpoint during `setSubjects`; the `emitFa4DataReady$`
      // effect below then emits the one-shot readiness beacon.
      tap(({ topicsTree, topicTocReverseMap, selectedLocation, currentSpineId, hints }) => {
        const metadata = this.topicMetadataStore.updateFromNavigationContext(
          topicsTree,
          topicTocReverseMap,
          selectedLocation,
          currentSpineId,
          hints,
        );

        if (!metadata) {
          emitDiagnostics('syncTopicMetadata$ unmatched (hydration disabled)', {
            currentSpineId,
            hints,
          });
        }
      }),
      logCatchError('syncTopicMetadata$'),
    );
  });

  /**
   * One-shot FA4 readiness beacon. After the two DFA API calls complete
   * (subjects list + bulk topics), the reverse map is populated, the spine is
   * known, and the bulk-hydration toggle is on, force an initial metadata
   * evaluation for the current TOC/spine location and emit
   * `READER.FA4_DATA.READY` (empty `{}` body) exactly once per book load so the
   * FA4 UI can reliably call `getCurrentLocation()`.
   */
  readonly emitFa4DataReady$ = this.effect(() => {
    return combineLatest([
      this.topicsStore.topicsTree$,
      this.topicsStore.topicTocReverseMap$,
      this.topicsStore.selectedLocation$,
      this.topicsStore.currentSpineId$,
      this.currentNavigationHints$,
      this.tocStore.activeTocItem$.pipe(startWith(undefined)),
      this.featureTogglesService.bulkHytHydration$,
      // Authoritative source for the currently-rendered spine. `topicsStore.currentSpineId$`
      // only gets set when `navigationActions.navigateTo` is dispatched (via
      // `setCurrentSpineItem$`), which does NOT happen on the reader's initial book
      // load — so on first load `topicsStore.currentSpineId$` can stay empty even
      // after the epub has rendered its first spine. `readerStore.spineItem$` is
      // populated as soon as the reader sets its initial state.
      this.readerStore.spineItem$,
    ]).pipe(
      filter(([, reverseMap, , currentSpineId, , activeTocItem, bulkOn, readerSpineItem]) => {
        const effectiveSpineId =
          currentSpineId ||
          (readerSpineItem)?.id ||
          (activeTocItem as any)?.spineItem?.id ||
          '';
        return bulkOn === true &&
          Boolean(effectiveSpineId) &&
          (reverseMap?.entries?.length ?? 0) > 0;
      }),
      auditTime(0, asyncScheduler),
      take(1),
      tap(([topicsTree, reverseMap, selectedLocation, currentSpineId, hints, activeTocItem, , readerSpineItem]) => {
        const effectiveSpineId =
          currentSpineId ||
          (readerSpineItem)?.id ||
          (activeTocItem as any)?.spineItem?.id ||
          '';

        const tocAnchorUuid =
          activeTocItem?.id && activeTocItem.id !== effectiveSpineId
            ? activeTocItem.id
            : undefined;

        const mergedHints: TopicResolutionHints = {
          ...hints,
          targetAnchorUuid: hints.targetAnchorUuid || tocAnchorUuid,
          targetTitle: hints.targetTitle || activeTocItem?.label,
          targetSpineId:
            hints.targetSpineId || activeTocItem?.spineItem?.id || effectiveSpineId,
        };

        this.topicMetadataStore.updateFromNavigationContext(
          topicsTree,
          reverseMap,
          selectedLocation,
          effectiveSpineId,
          mergedHints,
        );

        this.readiatorBridge.triggerFa4DataReady();
      }),
      // Pass resubscribe=false so a thrown resolver error stays silent (does
      // NOT re-fire this beacon). The default logCatchError behavior would
      // resubscribe to the source, causing infinite retries with our
      // synchronous inputs.
      logCatchError('emitFa4DataReady$', false),
    );
  });

  readonly getLocations$ = this.effect(() => {
    return merge(this.topicsActions$).pipe(
      ofType(topicsActions.getLocations),
      map((subject) => subject.uuid),
      withLatestFrom(
        this.readerConfigStore.subjectTopicsEndpoint$,
      ),
      switchMap(([subjectId, subjectTopicsEndpoint]) => {
        return this.getDfaLocations(subjectTopicsEndpoint, subjectId);
      }),
      map((t) => t),
      logCatchError('getLocations$'),
    );
  });

  readonly selectInternalLocation$ = this.effect(() =>
    this.topicsActions$.pipe(
      ofType(topicsActions.selectInternalLocation),
      map((action) => action.location),
      tap((location) => {
        this.topicsStore.dispatch(
          topicsActions.setSelectedLocation({ location }),
        );
        this.topicsStore.dispatch(topicsActions.setTopicInToc({ location }));
      }),
      tap(() => this.mediatorUtils.leftDrawer(false)),
      logCatchError('selectInternalLocation$'),
    ),
  );

  readonly setCurrentSpineItem$ = this.effect(() =>
    this.navigationActions$.pipe(
      ofType(navigationActions.navigateTo),
      withLatestFrom(this.readerStore.spineItem$),
      filter(([, spineItem]) => spineItem !== undefined),
      tap(([, spineItem]) => {
        this.topicsStore.setCurrentSpineId((spineItem as SpineItem).id);
        this.topicsStore.setSelectedLocation(null);
        emitDiagnostics('setCurrentSpineItem$', {
          newSpineId: (spineItem as SpineItem).id,
          newSpineIndex: (spineItem as SpineItem).index,
        });
      }),
      logCatchError('setCurrentSpineItem$'),
    ),
  );

  readonly navigateFromTocSelection$ = this.effect(() =>
    this.tocStore.activeTocItem$.pipe(
      filter((tocItem) => Boolean(tocItem)),
      withLatestFrom(
        this.topicsStore.topicsTree$,
        this.topicsStore.topicTocReverseMap$,
        this.topicsStore.selectedLocation$,
        this.topicsStore.currentSpineId$,
      ),
      tap(([tocItem, topicsTree, reverseMap, selectedLocation, currentSpineId]) => {
        if (!tocItem || !reverseMap || !topicsTree) {
          return;
        }

        // Try to find a matching topic via the reverse map
        const reverseMapEntry = reverseMap.entries.find(
          (entry) => entry.bestCandidate?.tocId === tocItem.id,
        );

        if (reverseMapEntry) {
          // Found a direct TOC ID match, navigate to this topic
          const topicNode = topicsTree[reverseMapEntry.topicId];
          if (topicNode) {
            this.topicsStore.setCurrentTopicNode({ node: topicNode });
            if (reverseMapEntry.location) {
              this.topicsStore.setSelectedLocation(reverseMapEntry.location);
            }
            emitDiagnostics('navigateFromTocSelection$', {
              tocItemId: tocItem.id,
              tocItemLabel: tocItem.label,
              matchedTopicId: reverseMapEntry.topicId,
              matchedTopicTitle: reverseMapEntry.topicTitle,
              matchType: reverseMapEntry.bestCandidate?.matchType,
            });
          }
        }
      }),
      logCatchError('navigateFromTocSelection$'),
    ),
  );

  readonly setSelectedLocation$ = this.effect(() => {
    return this.topicsActions$.pipe(
      ofType(topicsActions.setSelectedLocation),
      map((action) => action.location),
      withLatestFrom(this.readerStore.spineItem$),
      tap(([location, spineItem]) => {
        this.topicsStore.setCurrentSpineId((spineItem as SpineItem).id);
        this.topicsStore.setSelectedLocation(location);
        this.topicsStore.dispatch(topicsActions.setTopicInToc({ location }));
        emitDiagnostics('setSelectedLocation$', {
          selectedCfi: location?.cfi,
          selectedTitle: location?.title,
          currentSpineId: (spineItem as SpineItem).id,
        });
      }),
      logCatchError('setSelectedLocation$'),
    );
  });

  readonly setTocItem = this.effect(() => {
    return this.topicsActions$.pipe(
      ofType(topicsActions.setTopicInToc),
      map((action) => action.location),
      withLatestFrom(this.readerStore.flatToc$),
      tap(([location, flatToc]) => {
        //  Update TOC with topic location
        const nodeSpineId = this.epubLibCfiService.getSpineId(location?.cfi);
        const tocElement = Object.values(flatToc).find(
          (flatTocItem: { id }) => flatTocItem?.id === nodeSpineId,
        );
        if (!tocElement) {
          console.warn(
            'There is no ToC element for the current CFI:',
            location?.cfi,
          );
          return;
        }
        this.tocStore.dispatch(
          tocActions.setActiveTocItem({ tocItem: tocElement as TocItem }),
        );
      }),
    );
  });

  readonly navigateByDfaSearchLaunch = this.effect(() => {
    return this.topicsActions$.pipe(
      ofType(topicsActions.dfaSearchLaunch),
      map((action) => action.params),
      withLatestFrom(this.readerStore.flatToc$),
      tap(([params, toc]) => {
        const item = Object.values(toc).find(
          (item: TocItem) => item.id === params.spineID,
        ) as TocItem;
        if (item) {
          const node: DfaSearchNode = {
            spineIndex: item.spinePos,
            index: params.index,
            innerHTML: decodeURIComponent(params.text) || '',
          };
          this.elasticSearchService.handleSearchResultClicked(node);
        } else {
          this.mediatorUtils.navigate(0);
        }
      }),
      logCatchError('navigateByDfaSearchLaunch', false),
    );
  });

  // Sort DFA data returned from API
  sortDfaTags(tags: Tag[]): void {
    tags.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
  }

  private getUnhydratedSubjectIds(
    topicsTree: Record<string, { childIds?: string[], type?: TagType, loading?: boolean }>,
  ): string[] {
    const rootNode = topicsTree?.[Bonsai.RootNode];
    const subjectIds = rootNode?.childIds || [];

    return subjectIds.filter((subjectId: string) => {
      const subjectNode = topicsTree?.[subjectId];

      return Boolean(
        subjectNode?.type === TagType.SUBJECT &&
        !subjectNode.loading &&
        !this.hydratedSubjectIds.has(subjectId),
      );
    });
  }

  private hydrateSubjects(
    subjectTopicsEndpoint: string,
    subjectIds: string[],
  ): Observable<unknown> {
    if (!subjectTopicsEndpoint || subjectIds.length === 0) {
      return EMPTY;
    }

    subjectIds.forEach((subjectId) => {
      this.topicsStore.setSubjectLoadingState({ subjectId, loading: true });
    });

    return combineLatest(subjectIds.map((subjectId) => this.getDfaLocations(subjectTopicsEndpoint, subjectId)));
  }
}
